import { useMutation } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useGlobalToast } from '@providers/globalToastProvider';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPDATE_FLAG_NOTES } from '../utils/flags.queries';
import {
  hasUnsavedFlagNoteDraft,
  type ReconFlagNote,
} from '../utils/reconFlagNotes';
import type { ReconFlagRow } from '../utils/reconFlags.types';

export type ReconFlagsDrawerNotesProps = {
  flag: ReconFlagRow;
  onSaved: (notes: ReconFlagNote[]) => void | Promise<void>;
  /** When true, omits bottom margin and tightens spacing for use in the drawer footer. */
  embeddedInFooter?: boolean;
  /** When set with `onNotesDraftChange`, the field is controlled by the parent (e.g. drawer Resolve). */
  notesDraft?: string;
  onNotesDraftChange?: (next: string) => void;
  /** Hides the inline Save button. */
  hideSaveButton?: boolean;
  /** Hides the section heading (e.g. when the Notes tab label is sufficient). */
  hideHeading?: boolean;
  textFieldDisabled?: boolean;
};

export const ReconFlagsDrawerNotes = ({
  flag,
  onSaved,
  notesDraft: controlledDraft,
  onNotesDraftChange,
  hideSaveButton = false,
  hideHeading = false,
  textFieldDisabled = false,
  embeddedInFooter = false,
}: ReconFlagsDrawerNotesProps) => {
  const { t } = useTranslation(['reconciliation']);
  const { showToast } = useGlobalToast();
  const [internalDraft, setInternalDraft] = useState('');
  const [updateNotes, { loading }] = useMutation(UPDATE_FLAG_NOTES);

  const isControlled =
    controlledDraft !== undefined && onNotesDraftChange !== undefined;
  const draft = isControlled ? controlledDraft : internalDraft;
  const setDraft = isControlled ? onNotesDraftChange : setInternalDraft;

  useEffect(() => {
    if (!isControlled) {
      setInternalDraft('');
    }
  }, [flag.id, isControlled]);

  const onSave = useCallback(async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!hasUnsavedFlagNoteDraft(trimmed, flag.notes)) {
      return;
    }
    try {
      const { data, errors } = await updateNotes({
        variables: {
          input: { flagId: flag.id, notes: trimmed },
        },
      });
      if (errors?.length || !data?.updateFlagNotes?.flag?.id) {
        showToast({
          severity: 'error',
          message: t('reconFlags.drawer.notesSaveError'),
        });
        return;
      }
      showToast({
        severity: 'success',
        message: t('reconFlags.drawer.notesSaved'),
      });
      const savedNotes = data.updateFlagNotes.flag.notes ?? [];
      await onSaved(savedNotes);
    } catch {
      showToast({
        severity: 'error',
        message: t('reconFlags.drawer.notesSaveError'),
      });
    }
  }, [draft, flag.id, flag.notes, onSaved, showToast, t, updateNotes]);

  const showSave = !hideSaveButton;

  const minRows = embeddedInFooter ? 3 : 4;

  return (
    <Box sx={{ mb: embeddedInFooter ? 0 : 2 }}>
      {!hideHeading && (
        <Typography
          variant='titleMedium'
          sx={{ mb: embeddedInFooter ? 0.75 : 1 }}
        >
          {t('reconFlags.drawer.internalNotesHeading')}
        </Typography>
      )}
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        value={draft}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          setDraft(e.target.value)
        }
        placeholder={t('reconFlags.drawer.notesPlaceholder')}
        disabled={textFieldDisabled || loading}
      />
      {showSave && (
        <Box display='flex' justifyContent='flex-end' sx={{ mt: 1 }}>
          <Button
            label={t('reconFlags.drawer.saveNotes')}
            variant='filled'
            color='primary'
            loading={loading}
            disabled={loading || !hasUnsavedFlagNoteDraft(draft, flag.notes)}
            onClick={() => void onSave()}
          />
        </Box>
      )}
    </Box>
  );
};
